import isEqual from 'lodash-es/isEqual'
import isPlainObject from 'lodash-es/isPlainObject'
import * as amplitude from '@amplitude/analytics-browser'
import { onConsentReady, hasVendorGrant } from './gdpr_consent'
import { getEnvironment, inBrowser, inTestEnvironment } from 'shared/javascripts/util/environment'
import { deleteCookies } from 'shared/javascripts/util/cookies'
import { parsedFrontendConfig } from './frontend_config'

declare global {
  interface Window {
    amplitudeEvents: Array<[string, Record<string, unknown>]>
    amplitudeUserData: Array<Record<string, unknown>>
  }
}

const apiKey = parsedFrontendConfig('amplitudeApiKey') as string | null
let previousUserData: Record<string, unknown> = {}
let amplitudeInitialized = false

// Without a key (development, test) the events are recorded here instead, for specs to read.
if (!apiKey && inBrowser()) {
  window.amplitudeEvents = []
  window.amplitudeUserData = []
}

// amplitude.init() drops the device-id cookie as soon as it runs, so we must not call it before the
// user has granted consent (deferred-initialisation approach, see
// https://amplitude.com/docs/sdks/analytics/browser/cookies-and-consent-management). performAmplitudePush
// is only reached after the consent gate in amplitudePush(), so we initialise lazily on the first push
// to keep the cookie gated behind consent.
function ensureAmplitudeInitialized(): void {
  if (amplitudeInitialized) {
    return
  }
  amplitude.init(apiKey!, {
    // Replaces the legacy includeUtm/includeReferrer flags: `attribution` captures the UTM/referrer
    // params as user properties. We disable the other auto-tracking so the SDK does not emit event
    // types we never sent before — page views and interactions are tracked explicitly via trackEvent().
    // `session_id` is still attached to every event regardless of `sessions`; that flag only controls
    // the synthetic session_start/session_end events, which the legacy SDK did not send.
    defaultTracking: {
      attribution: true,
      sessions: false,
      pageViews: false,
      formInteractions: false,
      fileDownloads: false,
    },
    trackingOptions: {
      ipAddress: false,
    },
  })
  amplitudeInitialized = true
}

function performAmplitudePush(
  eventName: string,
  eventData: Record<string, unknown>,
  userData: Record<string, unknown>,
): void {
  if (apiKey) {
    ensureAmplitudeInitialized()
  }
  pushUserData(userData)
  const flatEventData = flattenData(eventData)
  if (ignoreAmplitudeEvent(eventName, flatEventData)) {
    return
  }
  if (getEnvironment() === 'development' || getEnvironment() === 'staging') {
    console.log('[Amplitude] Tracking event', { eventName, eventData, userData })
  }
  if (apiKey) {
    amplitude.track(eventName, flatEventData)
  } else {
    window.amplitudeEvents.push([eventName, flatEventData])
  }
}

export function amplitudePush(
  eventName: string,
  eventData: Record<string, unknown>,
  userData: Record<string, unknown>,
): void {
  onConsentReady(() => {
    if (hasVendorGrant('Amplitude')) {
      performAmplitudePush(eventName, eventData, userData)
    }
  })
}

export function updateAmplitudeConsent(): void {
  if (!apiKey) {
    return
  }
  if (!hasVendorGrant('Amplitude')) {
    deleteCookies((name) => name.startsWith('AMP_'))
  }
}

function pushUserData(userData: Record<string, unknown>): void {
  if (!isEqual(userData, previousUserData)) {
    if (apiKey) {
      const identify = new amplitude.Identify()
      for (const [key, value] of Object.entries(userData)) {
        identify.set(key, value as string | number | boolean)
      }
      amplitude.identify(identify)
    } else {
      window.amplitudeUserData.push(userData)
    }
    previousUserData = userData
  }
}

function flattenData(object: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const key in object) {
    const value = object[key]
    if (isPlainObject(value)) {
      const nestedObject = flattenData(value as Record<string, unknown>)
      for (const nestedKey in nestedObject) {
        result[`${key}.${nestedKey}`] = nestedObject[nestedKey]
      }
    } else {
      result[key] = value
    }
  }
  return result
}

export function ignoreAmplitudeEvent(
  eventName: string,
  eventData: Record<string, unknown>,
  testMode = false,
): boolean {
  function currentPageDisablesAmplitude(): boolean {
    const amplitudeMetaTag = document.head.querySelector(
      'meta[name="amplitude_enabled"]',
    ) as HTMLMetaElement | null
    if (amplitudeMetaTag) {
      return amplitudeMetaTag.content === 'false'
    } else {
      return false
    }
  }

  if (inTestEnvironment()) {
    return false
  }

  if (currentPageDisablesAmplitude()) {
    return true
  }

  const allowedEvents: Record<string, boolean | ((eventData: Record<string, unknown>) => boolean)> =
    {
      'page.view': (eventData) =>
        !!(eventData.path && /^\/ausbildung|\/jobs/.test(eventData.path as string)),
      'job.show.large': true,
      'jobSearch.results': true,
      'click.element': (eventData) =>
        !!(
          eventData['element.id'] &&
          (eventData['element.id'] as string).includes('career-card--apply-button')
        ),
    }

  const allowed = allowedEvents[eventName]

  return typeof allowed === 'function' ? !allowed(eventData) : !allowed
}
